import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useActivation } from "./ActivationContext";
import TourOverlay from "./TourOverlay";
import { useTourLayout } from "./useTourLayout";
import { useOverlayOpen } from "./useOverlayOpen";
import {
  getTourActionHint,
  isManualTourSubstep,
} from "./activationPath";
import { validateTourSubstep } from "./tourValidation";
import { useIsMobile } from "../hooks/useMediaQuery";

/** Floating step card + curved arrow + spotlight hole on the active target. */
export default function ActivationTour() {
  const {
    tourActive,
    mobileTourReplay,
    walkthroughReplay,
    currentStep,
    currentSubstep,
    currentSubsteps,
    currentSubstepId,
    completed,
    skipped,
    progressCount,
    totalSteps,
    dismiss,
    pauseTour,
    advanceSubstep,
  } = useActivation();
  const isMobile = useIsMobile(768);
  const overlayOpen = useOverlayOpen();

  const selector = currentSubstep?.target || null;
  const stepDone =
    currentStep &&
    (skipped.includes(currentStep.id) ||
      (!mobileTourReplay && completed.includes(currentStep.id)));

  const contentSubsteps = useMemo(
    () => currentSubsteps.filter((s) => s.kind !== "nav"),
    [currentSubsteps],
  );

  const subIdx = useMemo(() => {
    const list = contentSubsteps.length ? contentSubsteps : currentSubsteps;
    if (!list.length || !currentSubstepId) return 0;
    const idx = list.findIndex((x) => x.id === currentSubstepId);
    return Math.max(0, idx);
  }, [contentSubsteps, currentSubsteps, currentSubstepId]);

  const subTotal = contentSubsteps.length
    ? contentSubsteps.length
    : Math.max(1, currentSubsteps.length);

  const isNavStep = currentSubstep?.kind === "nav";
  const manualAdvance = isManualTourSubstep(currentSubstepId);
  const [advanceBlock, setAdvanceBlock] = useState("");

  useEffect(() => {
    setAdvanceBlock("");
  }, [currentSubstepId]);

  const handleAdvance = useCallback(() => {
    if (!currentStep?.id || !currentSubstepId) return;
    const mustValidate =
      !walkthroughReplay || currentSubstepId === "sched_fill";
    if (mustValidate) {
      const check = validateTourSubstep(currentStep.id, currentSubstepId);
      if (!check.ok) {
        setAdvanceBlock(check.message || "Заполните поле, чтобы продолжить");
        return;
      }
    }
    setAdvanceBlock("");
    advanceSubstep();
  }, [advanceSubstep, currentStep?.id, currentSubstepId, walkthroughReplay]);

  const layout = useTourLayout(
    tourActive && !stepDone && Boolean(currentStep && currentSubstep),
    selector,
    [currentSubstepId, overlayOpen, manualAdvance],
    { compact: !manualAdvance },
  );

  // Кнопки внизу экрана (сохранить расписание) — подсказка сверху, не перекрывает
  const forceTopBar =
    isMobile &&
    (currentSubstepId === "sched_save" ||
      currentSubstepId === "att_mark" ||
      (layout?.hole &&
        typeof window !== "undefined" &&
        layout.hole.top > window.innerHeight * 0.5));

  if (!tourActive || !currentStep || !currentSubstep || stepDone) return null;

  return (
    <TourOverlay
      visible
      layout={layout}
      title={currentSubstep.title}
      hint={currentSubstep.hint}
      isMobile={isMobile}
      isNavStep={isNavStep}
      contentFallback={isMobile}
      forceTopBar={forceTopBar}
      stepIndex={Math.min(progressCount + 1, totalSteps)}
      stepTotal={totalSteps}
      actionHint={getTourActionHint(currentSubstepId)}
      onHide={() => {
        if (isMobile) pauseTour();
        else dismiss();
      }}
      hideLabel={isMobile ? "Скрыть подсказку" : "Выйти из обучения"}
      onLater={() => dismiss()}
      onAdvance={handleAdvance}
      manualAdvance={manualAdvance}
      advanceBlock={advanceBlock}
      subIndex={subIdx + 1}
      subTotal={subTotal}
      kicker="Быстрый старт"
      overlayOpen={overlayOpen}
      arrowId="actTourArrowHead"
    />
  );
}
